import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IVendor, IVendorApi } from "common/interface/IVendor";
import { IWarehousePurchaseCode } from "common/interface/ISkuPurchaseCode";
import API, { apis } from "common/apis";
import { logoutIfNeeded } from "common/utils";
import { IApiError } from "common/interface/IApiError";
import { IPickerMappingUpload } from "common/interface/IPickerUploadScheme";
import { getPersistProcessingVendorWarehouseMapping, persistProcessingVendorWarehouseMapping, removePersistProcessingVendorWarehouseMapping } from "services/PersistService/PersistVendorServices";
import { IRequisitionUserMappingUpload } from "common/interface";
import { RootState } from "store/store";
import { getWarehouseId } from "services/PersistService/PersistWareHouseReturn";
import { Amendment, IAmendmentsApi, SubmitAmendmentRequest } from "common/interface/Amendment";
import { getApiErrorDetails } from "common/societyAmendmentUtils";

export interface State {
  vendors?: IVendorApi;
  status: "loading" | "success" | "error" | "idle" | "cancelling" | "saving";
  error?: string;
  processingVendorWarehouseMapping: IPickerMappingUpload;
  activePurchaseCodes: IWarehousePurchaseCode[];
  amendments: IAmendmentsApi;
  currentAmendment: Amendment | null;
  amendmentStatus: "loading" | "success" | "error" | "idle" | "saving";
  amendmentError?: string;
}

const initialState: State = {
  status: "idle",
  processingVendorWarehouseMapping: getPersistProcessingVendorWarehouseMapping(),
  activePurchaseCodes: [] as IWarehousePurchaseCode[],
  amendments: {
    pageNo: 1,
    pageSize: 25,
    pages: 0,
    total: 0,
    data: [],
  },
  currentAmendment: null,
  amendmentStatus: "idle",
};

export const getAllVendors = createAsyncThunk<
  IVendorApi,
  { pageNo: number; filter: string, pageSize: number }
>("vendors/all", async ({ pageNo = 1, filter, pageSize }, thunkApi) => {
  const filterParams = filter
    ? Object.fromEntries(new URLSearchParams(filter))
    : "";
  const response = await API.get(apis.vendors, {
    params: {
      pageNo,
      pageSize,
      ...filterParams,
    },
  })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const getVendorById = createAsyncThunk<
  IVendor,
  { vendorId: string }
>("vendors/id", async ({ vendorId }, thunkApi) => {
  const response = await API.get(apis.vendors + `/${vendorId}`)
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const saveVendor = createAsyncThunk<{}, IVendor>(
  "vendor/save",
  async (vendorFormValues, thunkApi) => {
    let response;
    if (vendorFormValues["id"] == undefined) {
      response = API.post(apis.vendors, vendorFormValues)
        .then((success) => success.data)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || "Something went wrong!";
          logoutIfNeeded(error, thunkApi);
          return thunkApi.rejectWithValue({ message: errorMessage });
        });
    }
    else {
      response = API.put(apis.vendors + `/${vendorFormValues['id']}`, vendorFormValues)
        .then((success) => success.data)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || "Something went wrong!";
          logoutIfNeeded(error, thunkApi);
          return thunkApi.rejectWithValue({ message: errorMessage });
        });
    }

    return response;
  }
);

export const getSupplyLocations = createAsyncThunk<
  string[],
  void,
  { rejectValue: { message: string } }
>("vendor/get-supply-locations", async (_, thunkApi) => {
  const response = await API.get(apis.getVendorSupplyLocations)
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });
  return response;
});

export const getActivePurchaseCodes = createAsyncThunk<
  IWarehousePurchaseCode[],
  void,
  { rejectValue: { message: string } }
>("vendor/get-purchase-codes", async (_, thunkApi) => {
  const response = await API.get(apis.masterSkus, {
    params: { purchaseSkuFilter: "ONLY" },
  })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });
  return Array.isArray(response) ? response : (response?.data ?? []);
});

export const onboardVendor = createAsyncThunk<
  {},
  Record<string, any>,
  { rejectValue: { message: string } }
>("vendor/onboard", async (data, thunkApi) => {
  const whId = getWarehouseId();
  const response = await API.post(apis.vendorOnboard, { ...data, whId })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });
  return response;
});

export const updateVendorB2c = createAsyncThunk<
  {},
  { vendorId: number; data: Record<string, any> },
  { rejectValue: { message: string } }
>("vendor/update-b2c", async ({ vendorId, data }, thunkApi) => {
  const whId = getWarehouseId();
  const response = await API.post(`${apis.vendors}`, { ...data, whId })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });
  return response;
});

export const updateVendorStatus = createAsyncThunk<
  {},
  {
    vendorId: number;
    isActive: boolean;
  }
>("vendor/update-status", async (payload, thunkApi) => {
  const response = await API.patch(
    `${apis.vendors}/${payload.vendorId}/status?status=${payload.isActive ? "ACTIVE" : "INACTIVE"}`,
    {
      "vendorId": payload.vendorId,
    },
  )
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});


export const saveProcessingVendorWarehouse = createAsyncThunk<IRequisitionUserMappingUpload>("vendor/upload/save", async (_, thunkApi) => {
  const state = thunkApi.getState() as RootState;
  const { vendors: { processingVendorWarehouseMapping: { key } } } = state;

  const response = await API.post(apis.saveVendorWarehouseMapping, {}, {
    params: { key }
  })
    .then(success => success.data)
    .catch(error => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const cancelProcessingVendorWarehouse = createAsyncThunk("vendor/upload/cancel", async (_, thunkApi) => {
  const state = thunkApi.getState() as RootState;
  const { vendors: { processingVendorWarehouseMapping: { key } } } = state;

  const response = await API.post(apis.saveVendorWarehouseMapping, {}, { //FixMe: Get Cancel API
    params: { key, cancel: 1 }
  })
    .then(success => success.data)
    .catch(error => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const submitVendorAmendment = createAsyncThunk(
  "vendor/submit-amendment",
  async (data: SubmitAmendmentRequest, thunkApi) => {
    try {
      const response = await API.post(apis.amendments, data);
      return response.data as Amendment;
    } catch (error) {
      const { message, code } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message, code });
    }
  }
);

export const getVendorAmendments = createAsyncThunk<
  IAmendmentsApi,
  {
    pageNo?: number;
    pageSize?: number;
    status?: string;
    requestType?: string;
    entityId?: string;
  }
>("vendor/get-amendments", async (params, thunkApi) => {
  try {
    const response = await API.get(`${apis.amendments}/paginated`, {
      params: {
        entityType: "VENDOR",
        pageNo: params.pageNo ?? 1,
        pageSize: params.pageSize ?? 25,
        ...(params.status ? { status: params.status } : {}),
        ...(params.requestType ? { requestType: params.requestType } : {}),
        ...(params.entityId ? { entityId: params.entityId } : {}),
      },
    });
    return response.data as IAmendmentsApi;
  } catch (error) {
    const { message } = getApiErrorDetails(error);
    logoutIfNeeded(error as any, thunkApi);
    return thunkApi.rejectWithValue({ message });
  }
});

export const getVendorAmendmentById = createAsyncThunk<Amendment, number>(
  "vendor/get-amendment-by-id",
  async (id, thunkApi) => {
    try {
      const response = await API.get(`${apis.amendments}/${id}`);
      return response.data as Amendment;
    } catch (error) {
      const { message } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message });
    }
  }
);

export const approveVendorAmendment = createAsyncThunk(
  "vendor/approve-amendment",
  async ({ id, remarks }: { id: number; remarks?: string }, thunkApi) => {
    try {
      const response = await API.put(`${apis.amendments}/${id}/approve`, { remarks });
      return response.data as Amendment;
    } catch (error) {
      const { message } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message });
    }
  }
);

export const rejectVendorAmendment = createAsyncThunk(
  "vendor/reject-amendment",
  async ({ id, remarks }: { id: number; remarks: string }, thunkApi) => {
    try {
      const response = await API.put(`${apis.amendments}/${id}/reject`, { remarks });
      return response.data as Amendment;
    } catch (error) {
      const { message, code } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message, code });
    }
  }
);

export const cancelVendorAmendment = createAsyncThunk(
  "vendor/cancel-amendment",
  async ({ id, remarks }: { id: number; remarks?: string }, thunkApi) => {
    try {
      const response = await API.put(`${apis.amendments}/${id}/cancel`, { remarks });
      return response.data as Amendment;
    } catch (error) {
      const { message } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message });
    }
  }
);

const vendorSlice = createSlice({
  name: "vendor",
  initialState,
  reducers: {
    setProcessingVendorWarehouseMapping: (state, action) => {
      state.status = "idle";
      state.error = "";
      state.processingVendorWarehouseMapping = action.payload;
      state.processingVendorWarehouseMapping.timeUploaded = new Date().toString();
      persistProcessingVendorWarehouseMapping(state.processingVendorWarehouseMapping);
    },
    removeProcessingVendorWarehouseMapping: (state) => {
      state.processingVendorWarehouseMapping = {};
      state.status = "idle";
      state.error = "";
      removePersistProcessingVendorWarehouseMapping();
    },
    resetAmendmentStatus: (state) => {
      state.amendmentStatus = "idle";
      state.amendmentError = "";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAllVendors.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllVendors.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        state.vendors = action.payload;
      })
      .addCase(getAllVendors.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";

        if (error.message) {
          state.error = error.message;
        }
      })

      .addCase(getVendorById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getVendorById.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";

        if (!state.vendors) {
          state.vendors = { data: [], pageNo: 1, pageSize: 25, pages: 1, total: 25 };
        }

        // if (state.vendors) {
        const vendorData = state.vendors?.data;
        const vendorInStore = vendorData.findIndex(
          (po) => po.id === action.payload.id
        );

        if (vendorInStore > -1) {
          const newPOs = [...state.vendors?.data];
          newPOs[vendorInStore] = {
            ...newPOs[vendorInStore],
            ...action.payload,
          };
          state.vendors.data = newPOs;
        } else if (vendorData) {
          vendorData.push(action.payload);
        } else {
          state.vendors.data = [action.payload];
        }
      })
      .addCase(getVendorById.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";

        if (error.message) {
          state.error = error.message;
        }
      })

      .addCase(saveVendor.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveVendor.fulfilled, (state) => {
        state.status = "success";
      })
      .addCase(saveVendor.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";

        if (error.message) {
          state.error = error.message;
        }
      })

      .addCase(getActivePurchaseCodes.fulfilled, (state, action) => {
        state.activePurchaseCodes = action.payload;
      })

      .addCase(onboardVendor.pending, (state) => {
        state.status = "loading";
      })
      .addCase(onboardVendor.fulfilled, (state) => {
        state.status = "success";
      })
      .addCase(onboardVendor.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(updateVendorB2c.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateVendorB2c.fulfilled, (state) => {
        state.status = "success";
      })
      .addCase(updateVendorB2c.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(updateVendorStatus.pending, (state) => {

        state.status = "saving";
      })
      .addCase(updateVendorStatus.fulfilled, (state, action) => {
        const temp = state.vendors;
        const index = temp!.data.findIndex(e => e.id == action.meta.arg.vendorId);
        temp!.data[index] = { ...temp!.data[index], status: action.meta.arg.isActive ? "ACTIVE" : "INACTIVE" };
        state.status = "idle";
      })
      .addCase(updateVendorStatus.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";

        if (error.message) {
          state.error = error.message;
        }
      })

      .addCase(submitVendorAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(submitVendorAmendment.fulfilled, (state, action) => {
        state.amendmentStatus = "success";
        state.amendmentError = "";

        // Reflect a submitted TOGGLE_STATUS request on the listed vendor right away:
        // show the requested status and lock the row until the request is resolved.
        const { requestType, entityId, payload } = action.meta.arg;
        if (requestType === "TOGGLE_STATUS" && entityId && state.vendors) {
          const index = state.vendors.data.findIndex((v) => String(v.id) === String(entityId));
          if (index > -1) {
            state.vendors.data[index] = {
              ...state.vendors.data[index],
              status: payload.status as string,
              pendingAmendmentId: action.payload.id,
            };
          }
        }
      })
      .addCase(submitVendorAmendment.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.amendmentStatus = "error";
        state.amendmentError = error?.message;
      })

      .addCase(getVendorAmendments.pending, (state) => {
        state.amendmentStatus = "loading";
      })
      .addCase(getVendorAmendments.fulfilled, (state, action) => {
        state.amendmentStatus = "idle";
        state.amendments = action.payload;
        state.amendmentError = "";
      })
      .addCase(getVendorAmendments.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.amendmentStatus = "error";
        state.amendmentError = error?.message;
      })

      .addCase(getVendorAmendmentById.pending, (state) => {
        state.amendmentStatus = "loading";
      })
      .addCase(getVendorAmendmentById.fulfilled, (state, action) => {
        state.amendmentStatus = "idle";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(getVendorAmendmentById.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.amendmentStatus = "error";
        state.amendmentError = error?.message;
        state.currentAmendment = null;
      })

      .addCase(approveVendorAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(approveVendorAmendment.fulfilled, (state, action) => {
        state.amendmentStatus = "success";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(approveVendorAmendment.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.amendmentStatus = "error";
        state.amendmentError = error?.message;
      })

      .addCase(rejectVendorAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(rejectVendorAmendment.fulfilled, (state, action) => {
        state.amendmentStatus = "success";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(rejectVendorAmendment.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.amendmentStatus = "error";
        state.amendmentError = error?.message;
      })

      .addCase(cancelVendorAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(cancelVendorAmendment.fulfilled, (state, action) => {
        state.amendmentStatus = "success";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(cancelVendorAmendment.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.amendmentStatus = "error";
        state.amendmentError = error?.message;
      })

      /**
      * SAVE PROJECTED SR
      **/
      .addCase(saveProcessingVendorWarehouse.pending, state => {
        state.status = "loading";
      })
      .addCase(saveProcessingVendorWarehouse.fulfilled, state => {
        state.status = "success";
        state.error = "";
        state.processingVendorWarehouseMapping = {};
        removeProcessingVendorWarehouseMapping();
      })
      .addCase(saveProcessingVendorWarehouse.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";

        if (error.message) {
          state.error = error.message;
        }
      })
      /**
      * GET SUPPLY LOCATIONS
      **/
      // .addCase(getSupplyLocations.fulfilled, state => {
      //   state.status = "success";
      //   state.error = "";
      // })
      // .addCase(getSupplyLocations.rejected, (state, action) => {
      //   const error = action.payload as IApiError;
      //   state.status = "error";

      //   if (error.message) {
      //     state.error = error.message;
      //   }
      // })

      /**
       * CANCEL PROJECTED SR
      **/
      .addCase(cancelProcessingVendorWarehouse.pending, state => {
        state.status = "cancelling";
      })
      .addCase(cancelProcessingVendorWarehouse.fulfilled, state => {
        state.status = "idle";
        state.error = "";
        state.processingVendorWarehouseMapping = {};
        removeProcessingVendorWarehouseMapping();
      })
      .addCase(cancelProcessingVendorWarehouse.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) {
          state.error = error.message;
        }
      })
  },
});

export const {
  removeProcessingVendorWarehouseMapping,
  setProcessingVendorWarehouseMapping,
  resetAmendmentStatus,
} = vendorSlice.actions;

export default vendorSlice.reducer;
